<template>
  <span v-if="hasDefaultSlotContent" ref="slotWrapper">
    <!-- @slot Slot for custom svg icon. -->
    <slot />
  </span>
  <component
    :is="iconComponent"
    v-else
    :aria-label="label"
    :aria-hidden="ariaHiddenAttribute"
    :role="roleAttribute"
    :qaid="qaid"
    class="pdl-icon inline-block align-middle fill-current"
    :style="iconSizeStyle"
  />
</template>

<script>
import {shallowRef, defineAsyncComponent} from 'vue';
import {PdlSlotUtil} from '@pedal/pdl-shared-libraries';

// Use import.meta.glob to statically import all icon components for Vite compatibility
const icons = import.meta.glob('./icons/Icon*.vue');
 
const iconSizes = {
  14: '14',
  18: '18',
  24: '24',
  32: '32',
  48: '48',
};

export default {
  name: 'PdlIcon',
  props: {
    /**
     * The name of the icon to display. For a complete list of icons available, visit the <a href="/?path=/docs/fundamentals-iconography--default-story">Iconography page</a>.
     */
    name: {
      type: String,
      default: null,
    },
    /**
     * The size of the icon (in px). (`14`, `18`, `24`, `32` or `48`).
     */
    size: {
      type: String,
      default: '24',
    },
    /**
     * Text used to describe the context of the icon. A label is required when there is no pairing of visible text with an icon. The text will be added to an `aria-label` and give the icon a `role="img"`. An empty string marks the icon as decorative, with a `role="presentation"` and `aria-hidden="true"`.
     */
    label: {
      type: String,
      default: null,
    },
    /**
     * A `qaid` is used as a unique string, serving as a hook for automated tests.
     */
    qaid: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      iconComponent: shallowRef(undefined),
    };
  },
  computed: {
    iconSizeStyle() {
      if (!this.size || !iconSizes[this.size]) return;
      return `--pdl-icon-size: ${iconSizes[this.size]}px;`;
    },
    hasDefaultSlotContent() {
      return PdlSlotUtil.hasSlotContent(this.$slots?.default);
    },
    roleAttribute() {
      return this.label ? 'img' : 'presentation';
    },
    ariaHiddenAttribute() {
      return this.label ? false : true;
    },
    formattedName() {
      if (!this.name) return;
      return this.name
        .split('_')
        .map((segment) => segment?.charAt(0).toUpperCase() + segment.slice(1))
        .join('');
    },
  },
  mounted() {
    if (!this.hasDefaultSlotContent) return;

    //grab slot content
    const slotElement = this.$refs.slotWrapper?.children?.[0];
    if (!slotElement) return;
    //apply computed properties to slot content root node
    this.label && slotElement.setAttribute('aria-label', this.label);
    this.ariaHiddenAttribute && slotElement.setAttribute('aria-hidden', this.ariaHiddenAttribute);
    this.roleAttribute && slotElement.setAttribute('role', this.roleAttribute);
    this.qaid && slotElement.setAttribute('qaid', this.qaid);
    if (this.size) {
      slotElement.setAttribute('width', this.size);
      slotElement.setAttribute('height', this.size);
    }
  },
  watch: {
    formattedName: {
      immediate: true,
      handler() {
          const iconPath = `./icons/Icon${this.formattedName}.vue`;
          if (icons?.[iconPath]) {
            this.iconComponent = defineAsyncComponent(icons[iconPath]);
          } else{
          // Fallback for non-Vite environments
          this.iconComponent = defineAsyncComponent(() => import(`./icons/Icon${this.formattedName}.vue`));
        }
      }
    },
  },
};
</script>

<style lang="scss">
.pdl-icon {
  width: var(--pdl-icon-size, 24px);
  height: var(--pdl-icon-size, 24px);
}
</style>
